import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FolderKanban, Plus, Loader2, ArrowLeft,
  CheckCircle2, Clock, Trash2,
  Target, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  low: { bg: "bg-[#10B981]/10", text: "text-[#10B981]", label: "Basse" },
  medium: { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]", label: "Moyenne" },
  high: { bg: "bg-[#EF4444]/10", text: "text-[#EF4444]", label: "Haute" },
  urgent: { bg: "bg-[#DC2626]/10", text: "text-[#DC2626]", label: "Urgente" },
};

export function ProjectsListPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", client_name: "", color: "#0099CC" });
  const [creating, setCreating] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/projects`, { headers });
      setProjects(res.data);
    } catch { } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Nom obligatoire"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/projects`, form, { headers });
      navigate(`/app/projets/${res.data.id}`);
      toast.success("Projet cree");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setCreating(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#10B981]" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="projects-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3"><FolderKanban className="w-6 h-6 text-[#10B981]" /> GLOTECH Projects</h1>
          <p className="text-sm text-[#64748b] mt-1">{projects.length} projet{projects.length > 1 ? "s" : ""}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#10B981] hover:bg-[#0d9668] text-white" data-testid="create-project-btn"><Plus className="w-4 h-4 mr-2" /> Nouveau projet</Button>
      </div>
      {showCreate && (
        <div className="bg-[#0B0F19] border border-[#10B981]/20 rounded-xl p-5 space-y-4" data-testid="create-project-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-[#64748b] mb-1 block">Nom du projet *</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Famille Espoir RDC" className="bg-[#060B14] border-[#1E293B] text-white" data-testid="project-name-input" /></div>
            <div><label className="text-xs text-[#64748b] mb-1 block">Client</label><Input value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} placeholder="Nom du client" className="bg-[#060B14] border-[#1E293B] text-white" /></div>
          </div>
          <div><label className="text-xs text-[#64748b] mb-1 block">Description</label><textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} className="w-full bg-[#060B14] border border-[#1E293B] rounded-lg p-3 text-white text-sm resize-none" /></div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-[#64748b]">Annuler</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#10B981] text-white" data-testid="submit-create-project">{creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Creer</Button>
          </div>
        </div>
      )}
      {projects.length === 0 ? (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-12 text-center"><FolderKanban className="w-12 h-12 text-[#334155] mx-auto mb-4" /><h2 className="text-lg font-semibold text-white mb-2">Aucun projet</h2><p className="text-sm text-[#64748b]">Creez votre premier projet pour commencer</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p => (
            <button key={p.id} onClick={() => navigate(`/app/projets/${p.id}`)} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5 text-left hover:border-[#10B981]/30 transition-all group" data-testid={`project-card-${p.id}`}>
              <div className="flex items-center gap-3 mb-3"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color || "#10B981" }} /><h3 className="text-white font-semibold truncate flex-1">{p.name}</h3><ChevronRight className="w-4 h-4 text-[#334155] group-hover:text-[#10B981]" /></div>
              {p.client_name && <p className="text-xs text-[#64748b] mb-2">Client: {p.client_name}</p>}
              {p.description && <p className="text-xs text-[#64748b] line-clamp-2 mb-3">{p.description}</p>}
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status === "active" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#64748B]/10 text-[#64748B]"}`}>{p.status === "active" ? "Actif" : p.status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");
  const [showAddTask, setShowAddTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: "", priority: "medium" });
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [msForm, setMsForm] = useState({ title: "", due_date: "", description: "" });

  const fetchProject = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/projects/${projectId}`, { headers }); setProject(res.data); } catch { toast.error("Erreur chargement projet"); } finally { setLoading(false); }
  }, [projectId, token]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  const addTask = async (columnId) => {
    if (!taskForm.title.trim()) return;
    try { await axios.post(`${API}/api/projects/${projectId}/tasks`, { ...taskForm, column_id: columnId, status: "todo" }, { headers }); setTaskForm({ title: "", priority: "medium" }); setShowAddTask(null); fetchProject(); } catch { toast.error("Erreur"); }
  };

  const updateTask = async (taskId, updates) => {
    try { await axios.patch(`${API}/api/projects/${projectId}/tasks/${taskId}`, updates, { headers }); fetchProject(); } catch { toast.error("Erreur"); }
  };

  const deleteTask = async (taskId) => {
    try { await axios.delete(`${API}/api/projects/${projectId}/tasks/${taskId}`, { headers }); fetchProject(); } catch { toast.error("Erreur"); }
  };

  const addMilestone = async () => {
    if (!msForm.title.trim()) return;
    try { await axios.post(`${API}/api/projects/${projectId}/milestones`, msForm, { headers }); setMsForm({ title: "", due_date: "", description: "" }); setShowMilestoneForm(false); fetchProject(); } catch { toast.error("Erreur"); }
  };

  const updateMilestone = async (msId, updates) => {
    try { await axios.patch(`${API}/api/projects/${projectId}/milestones/${msId}`, updates, { headers }); fetchProject(); } catch { toast.error("Erreur"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#10B981]" /></div>;
  if (!project) return null;

  const tasks = project.tasks || [];
  const columns = project.columns || [];
  const milestones = project.milestones || [];
  const stats = project.stats || {};

  return (
    <div className="space-y-4" data-testid="project-detail-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="ghost" onClick={() => navigate("/app/projets")} className="text-[#64748b] p-2" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-white truncate flex items-center gap-2"><div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: project.color }} />{project.name}</h1>
          {project.client_name && <p className="text-xs text-[#64748b]">Client: {project.client_name}</p>}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right"><span className="text-lg font-bold text-[#10B981]">{stats.progress_percent}%</span><p className="text-[10px] text-[#64748b]">{stats.done_tasks}/{stats.total_tasks} taches</p></div>
          <div className="w-20 h-2 bg-[#1E293B] rounded-full overflow-hidden"><div className="h-full bg-[#10B981] rounded-full" style={{ width: `${stats.progress_percent}%` }} /></div>
        </div>
      </div>
      <div className="flex gap-1 bg-[#0B0F19] rounded-lg p-1 w-fit">
        {["kanban", "list", "timeline"].map(v => (
          <button key={v} onClick={() => setView(v)} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${view === v ? "bg-[#10B981] text-white" : "text-[#64748b] hover:text-white"}`} data-testid={`view-tab-${v}`}>{v === "kanban" ? "Kanban" : v === "list" ? "Liste" : "Timeline"}</button>
        ))}
      </div>

      {view === "kanban" && (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
          {columns.map(col => {
            const colTasks = tasks.filter(t => t.column_id === col.id);
            return (
              <div key={col.id} className="bg-[#0B0F19] rounded-xl p-3 min-w-[280px] w-[280px] shrink-0 border border-[#1E293B]">
                <div className="flex items-center justify-between mb-3 px-1"><span className="text-sm font-semibold text-white">{col.name}</span><span className="text-xs text-[#64748b] bg-[#1E293B] px-2 py-0.5 rounded-full">{colTasks.length}</span></div>
                <div className="space-y-2 min-h-[100px]">
                  {colTasks.map(task => {
                    const prio = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                    return (
                      <div key={task.id} className="bg-[#060B14] border border-[#1E293B] rounded-lg p-3 group hover:border-[#10B981]/30 transition-all" data-testid={`task-card-${task.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-white font-medium flex-1">{task.title}</p>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {col.order < columns.length - 1 && <button onClick={() => updateTask(task.id, { column_id: columns[col.order + 1]?.id })} className="text-[#64748b] hover:text-[#10B981]"><ChevronRight className="w-3.5 h-3.5" /></button>}
                            <button onClick={() => deleteTask(task.id)} className="text-[#64748b] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${prio.bg} ${prio.text}`}>{prio.label}</span>
                          {task.due_date && <span className="text-[10px] text-[#64748b] flex items-center gap-1"><Clock className="w-3 h-3" />{task.due_date.split("T")[0]}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {showAddTask === col.id ? (
                  <div className="mt-2 space-y-2">
                    <Input value={taskForm.title} onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))} placeholder="Titre de la tache" className="bg-[#060B14] border-[#1E293B] text-white text-sm h-8" data-testid={`task-title-input-${col.id}`} autoFocus onKeyDown={e => e.key === "Enter" && addTask(col.id)} />
                    <div className="flex gap-1"><Button size="sm" onClick={() => addTask(col.id)} className="bg-[#10B981] text-white text-xs h-7" data-testid={`add-task-submit-${col.id}`}>Ajouter</Button><Button size="sm" variant="ghost" onClick={() => setShowAddTask(null)} className="text-[#64748b] text-xs h-7">Annuler</Button></div>
                  </div>
                ) : (
                  <button onClick={() => setShowAddTask(col.id)} className="w-full mt-2 text-xs text-[#64748b] hover:text-[#10B981] flex items-center gap-1 justify-center py-2 rounded-lg hover:bg-[#10B981]/5" data-testid={`add-task-btn-${col.id}`}><Plus className="w-3.5 h-3.5" /> Ajouter</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === "list" && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl overflow-hidden" data-testid="list-view">
          <table className="w-full"><thead><tr className="border-b border-[#1E293B] text-[10px] uppercase tracking-wider text-[#64748b]"><th className="text-left p-3">Tache</th><th className="text-left p-3">Colonne</th><th className="text-left p-3">Priorite</th><th className="text-left p-3">Date</th><th className="p-3"></th></tr></thead>
          <tbody>{tasks.map(task => { const prio = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium; const col = columns.find(c => c.id === task.column_id); return (
            <tr key={task.id} className="border-b border-[#0B0F19] hover:bg-[#060B14]"><td className="p-3 text-sm text-white">{task.title}</td><td className="p-3 text-xs text-[#64748b]">{col?.name || "-"}</td><td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded ${prio.bg} ${prio.text}`}>{prio.label}</span></td><td className="p-3 text-xs text-[#64748b]">{task.due_date ? task.due_date.split("T")[0] : "-"}</td><td className="p-3"><button onClick={() => deleteTask(task.id)} className="text-[#334155] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button></td></tr>
          ); })}{tasks.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-[#64748b]">Aucune tache</td></tr>}</tbody></table>
        </div>
      )}

      {view === "timeline" && (
        <div className="space-y-4" data-testid="timeline-view">
          <div className="flex items-center justify-between"><h3 className="text-sm font-semibold text-white flex items-center gap-2"><Target className="w-4 h-4 text-[#10B981]" /> Jalons et livrables</h3><Button size="sm" onClick={() => setShowMilestoneForm(true)} className="bg-[#10B981] text-white text-xs" data-testid="add-milestone-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un jalon</Button></div>
          {showMilestoneForm && (
            <div className="bg-[#0B0F19] border border-[#10B981]/20 rounded-xl p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><Input value={msForm.title} onChange={e => setMsForm(p => ({ ...p, title: e.target.value }))} placeholder="Titre du jalon" className="bg-[#060B14] border-[#1E293B] text-white" data-testid="milestone-title-input" /><Input type="date" value={msForm.due_date} onChange={e => setMsForm(p => ({ ...p, due_date: e.target.value }))} className="bg-[#060B14] border-[#1E293B] text-white" /></div>
              <div className="flex gap-2 justify-end"><Button variant="ghost" onClick={() => setShowMilestoneForm(false)} className="text-[#64748b]" size="sm">Annuler</Button><Button onClick={addMilestone} className="bg-[#10B981] text-white" size="sm" data-testid="submit-milestone">Creer</Button></div>
            </div>
          )}
          {milestones.length === 0 ? (
            <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-8 text-center"><Target className="w-10 h-10 text-[#334155] mx-auto mb-3" /><p className="text-sm text-[#64748b]">Aucun jalon. Ajoutez des jalons pour suivre l'avancement.</p></div>
          ) : (
            <div className="space-y-0">{milestones.map((ms, i) => {
              const msTasks = tasks.filter(t => t.milestone_id === ms.id);
              const doneTasks = msTasks.filter(t => t.status === "done").length;
              const msProgress = msTasks.length > 0 ? Math.round(doneTasks / msTasks.length * 100) : 0;
              return (
                <div key={ms.id} className="flex gap-4" data-testid={`milestone-${ms.id}`}>
                  <div className="flex flex-col items-center"><div className={`w-4 h-4 rounded-full border-2 shrink-0 ${ms.status === "completed" ? "bg-[#10B981] border-[#10B981]" : "bg-[#0B0F19] border-[#64748b]"}`} />{i < milestones.length - 1 && <div className="w-0.5 flex-1 bg-[#1E293B] min-h-[60px]" />}</div>
                  <div className="flex-1 pb-6"><div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2"><h4 className="text-sm font-semibold text-white">{ms.title}</h4><div className="flex items-center gap-2">{ms.due_date && <span className="text-[10px] text-[#64748b]">{ms.due_date}</span>}<button onClick={() => updateMilestone(ms.id, { status: ms.status === "completed" ? "pending" : "completed" })} className={`text-xs px-2 py-0.5 rounded-full ${ms.status === "completed" ? "bg-[#10B981]/20 text-[#10B981]" : "bg-[#1E293B] text-[#64748b]"}`}>{ms.status === "completed" ? "Termine" : "Marquer termine"}</button></div></div>
                    {msTasks.length > 0 && <div className="flex items-center gap-2"><div className="flex-1 h-1.5 bg-[#1E293B] rounded-full overflow-hidden"><div className="h-full bg-[#10B981] rounded-full" style={{ width: `${msProgress}%` }} /></div><span className="text-[10px] text-[#64748b]">{doneTasks}/{msTasks.length}</span></div>}
                  </div></div>
                </div>
              );
            })}</div>
          )}
        </div>
      )}
    </div>
  );
}
